import { injectable } from "inversify";
import { IContainerResource } from "@/interfaces/IContainerResource";
import { IEmploymentHttpClient } from "@/interfaces/IEmploymentService";
import {
  ContainerResponse,
  PositionPermission,
  PositionPermissionRequest,
  PositionRequest,
  PositionResponse,
  UserExportRequest,
} from "../models/dtos";

import * as dtos from "../models/dtos";
import { ContainerUser } from "@/models/ContainerUser";
import CustomThemeBuilder from "@/features/shared/models/CustomThemeBuilder";
@injectable()
export class ContainerResource implements IContainerResource {
  _client: IEmploymentHttpClient;
  themeBuilder = new CustomThemeBuilder();
  constructor(client: IEmploymentHttpClient) {
    this._client = client;
  }
  async upsertContainerTemplate(
    payload: dtos.ContainerTemplateResponse
  ): Promise<any> {
    const data = {
      action: "upsert_ContainerTemplate",
      data: {
        containerId: payload.containerId,
        id: payload.id,
        text: payload.text,
        title: payload.title,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async getContainerTemplates(
    containerId: number
  ): Promise<dtos.ContainerTemplateResponse[]> {
    const data = {
      action: "get_ContainerTemplates",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data as dtos.ContainerTemplateResponse[];
  }
  async deleteNotificationByContainerId(containerId: number): Promise<any> {
    const data = {
      action: "delete_NotificationByContainerId",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async deleteContainerAsset(
    containerId: number,
    folderId: number,
    id: number
  ): Promise<any> {
    const data = {
      action: "delete_ContainerAsset",
      data: {
        folderId: folderId,
        containerId: containerId,
        id: id,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async createContainerAsset(
    id: number,
    containerId: number,
    folderId: number,
    data: string,
    contentType: string,
    name: string
  ): Promise<number> {
    const data2 = {
      action: "create_containerAssetCommand",
      data: {
        id: id,
        folderId: folderId,
        containerId: containerId,
        data: data,
        contentType: contentType,
        name: name,
      },
    };
    const response = await this._client.post(data2);
    return response.data as number;
  }
  async createPositionTemplateAsset(
    id: number,
    containerId: number,
    folderId: number,
    data: string,
    contentType: string,
    name: string
  ): Promise<number> {
    const data2 = {
      action: "create_positionTemplateAssetCommand",
      data: {
        id: id,
        folderId: folderId,
        containerId: containerId,
        data: data,
        contentType: contentType,
        name: name,
      },
    };
    const response = await this._client.post(data2);
    return response.data as number;
  }
  async createFolder(containerId: number, name: string): Promise<number> {
    const data2 = {
      action: "create_Folder",
      data: {
        containerId: containerId,
        name: name,
      },
    };
    const response = await this._client.post(data2);
    return response.data as number;
  }
  async getFolder(
    containerId: number,
    id: number
  ): Promise<dtos.FolderResponse> {
    const data2 = {
      action: "get_folder",
      data: {
        containerId: containerId,
        id: id,
      },
    };
    const response = await this._client.post(data2);
    return response.data as dtos.FolderResponse;
  }
  async getFolders(containerId: number): Promise<dtos.FolderResponse[]> {
    const data2 = {
      action: "get_folders",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data2);
    return response.data as dtos.FolderResponse[];
  }
  async createPositionType(name: string, containerId: number): Promise<any> {
    const data = {
      action: "create_positionType",
      data: {
        name: name,
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async delete(id: number): Promise<any> {
    const data = {
      action: "delete_container",
      data: {
        id: id,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async create(name: string, plans: string[]): Promise<any> {
    const data = {
      action: "create_container",
      data: {
        name: name,
        plans: plans,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async updateAccount(user: ContainerUser): Promise<any> {
    const data = {
      action: "update_account",
      data: {
        email: user.Email,
        firstname: user.Firstname,
        lastname: user.Lastname,
        id: user.UserId,
        phoneNumber: user.PhoneNumber,
        title: user.Title,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async updateMetadata(
    item: dtos.UpdateContainerMetadataRequest
  ): Promise<any> {
    const data = {
      action: "create_containerMetadata",
      data: {
        id: item.id,
        metadata: item.metadata,
        theme: item.theme,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async search(
    query: string,
    containerId: number
  ): Promise<dtos.ApplicantPositionResponse[]> {
    const data = {
      action: "get_applicantPositionBySearchQuery",
      data: {
        containerId: containerId,
      },
    };

    const response = await this._client.postUrl(
      "/api/dispatcher?filter=" + query,
      data
    );

    return response.data as dtos.ApplicantPositionResponse[];
  }
  async updateContainerUserPermission(
    payload: dtos.UpdateContainerUserPermission
  ): Promise<any> {
    const data = {
      action: "update_ContainerUserPermissionsCommand",
      data: {
        containerId: payload.containerId,
        email: payload.email,
        permissionIds: payload.permissionIds,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async updateLabel(payload: dtos.LabelTemplate): Promise<any> {
    const data = {
      action: "update_containerTemplate",
      data: {
        containerId: payload.containerId,
        labelId: payload.labelId,
        name: payload.name,
        subject: payload.subject,
        content: payload.content,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  createClient(token: string) {
    this._client.createClient(token, true);
  }
  async updateContainerTemplate(
    containerId: number,
    labelId: number,
    name: string,
    subject: string,
    content: string
  ): Promise<any> {
    const data = {
      action: "update_containerTemplate",
      data: {
        containerId: containerId,
        labelId: labelId,
        name: name,
        subject: subject,
        content: content,
      },
    };
    const response = await this._client.post(data);
  }

  async getContainers() {
    const data = {
      action: "get_containers",
      data: {},
    };
    const response = await this._client.post(data);
    return response.data as ContainerResponse[];
  }

  async getById(id: number) {
    const data = {
      action: "get_container",
      data: {
        id: id,
      },
    };
    const response = await this._client.post(data);
    const dto = response.data as ContainerResponse;
    if (dto && !dto.theme) {
      dto.theme = this.themeBuilder.getDefaultTheme();
    }
    return dto;
  }
  async resetPassword(email: string) {
    const url = "/api/account/reset";
    const data = { email: email };
    return await this._client.postUrl(url, data);
  }
  async getPositionsByContainer(id: string) {
    const data = {
      action: "get_positions_by_container",
      data: {
        name: id,
      },
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }

  async createPosition(payload: PositionRequest) {
    const data = {
      action: "create_position",
      data: payload,
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }
  async createInternalPosition(payload: PositionRequest) {
    const data = {
      action: "create_position",
      data: { ...payload, isInternal: true },
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }

  async updatePosition(payload: any) {
    const data = {
      action: "update_position",
      data: payload,
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }
  async updateInternalPosition(payload: any) {
    const data = {
      action: "update_position",
      data: { ...payload, isInternal: true },
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }

  async getPosition(id: number) {
    const data = {
      action: "get_position",
      data: {
        id: id,
      },
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }

  async getPositions(containerId: number) {
    const data = {
      action: "get_positions",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data as PositionResponse[];
  }

  async createUserContainerPermission(
    payload: dtos.CreateContainerUserPermission
  ) {
    const data = {
      action: "create_userContainerPermission",
      data: {
        email: payload.email,
        containerId: payload.containerId,
        permissionId: payload.permissionId,
      },
    };
    const response = await this._client.post(data);
    return response.data as dtos.CreateContainerUserPermissionResponse;
  }
  async deleteUserContainerPermission(
    payload: dtos.DeleteContainerUserPermission
  ) {
    const data = {
      action: "delete_userContainerPermission",
      data: {
        id: payload.id,
        containerId: payload.containerId,
      },
    };
    return this._client.post(data);
  }
  async createPositionPermission(payload: PositionPermission) {
    const data = {
      action: "create_positionPermission",
      data: {
        email: payload.Email,
        positionId: payload.Position.id,
        permissionId: payload.PermissionId,
      },
    };
    return this._client.post(data);
  }

  async updatePositionPermission(payload: PositionPermissionRequest) {
    const data = {
      action: "create_positionPermission",
      data: payload,
    };
    return this._client.post(data);
  }

  async deletePositionPermission(payload: PositionPermission) {
    const data = {
      action: "delete_positionPermission",
      data: {
        email: payload.Email,
        positionId: payload.Position.id,
      },
    };
    return this._client.post(data);
  }
  async getEmailVariables(): Promise<any> {
    const data = {
      action: "get_TemplateProperties",
      data: {},
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async deleteApplicantFromContainer(
    payload: dtos.ContainerApplicant
  ): Promise<any> {
    const data = {
      action: "delete_ApplicantFromContainer",
      data: {
        userId: payload.userId,
        containerId: payload.containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data as any;
  }
  async getUserExport(payload: UserExportRequest) {
    const data = {
      action: "create_userExport",
      data: {
        email: payload.userEmail,
        containerId: payload.containerId,
      },
    };
    return await this._client.post(data);
  }
  async getContainerSkills(containerId: number) {
    const data = {
      action: "get_containerSkills",
      data: {
        containerId: containerId,
      },
    };
    return this._client.post(data);
  }
  async createDemoContainer(payload: dtos.CreateDemoContainerRequest) {
    const data = {
      action: "create_demoContainer",
      data: payload,
    };
    return this._client.post(data);
  }
  async getCompetences(containerId: number) {
    const data = {
      action: "get_competences",
      data: {
        containerId: containerId,
      },
    };
    return this._client.post(data);
  }
  async upsertSkill(payload: dtos.UpsertSkillRequest) {
    const data = {
      action: "add_skillCommand",
      data: payload,
    };
    return this._client.post(data);
  }
  async deleteSkill(payload: dtos.DeleteSkillRequest) {
    const data = {
      action: "delete_skill",
      data: payload,
    };
    return this._client.post(data);
  }
  async getIcons(containerId: number) {
    const data = {
      action: "get_skillsIcons",
      data: {
        containerId: containerId,
      },
    };
    return this._client.post(data);
  }
  async getSkillCollections(containerId: number) {
    const data = {
      action: "get_competenceTemplates",
      data: {
        containerId: containerId,
      },
    };
    return this._client.post(data);
  }
  async upsertSkillCollection(payload: dtos.UpsertSkillCollection) {
    const data = {
      action: "add_skillTemplate",
      data: payload,
    };
    return this._client.post(data);
  }
  async deleteSkillCollection(payload: dtos.DeleteSkillCollection) {
    const data = {
      action: "delete_skillTemplate",
      data: payload,
    };
    return this._client.post(data);
  }
  async getEmailTemplates(cotnainerId: number) {
    const data = {
      action: "get_emailTemplates",
      data: {
        containerId: cotnainerId,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }
  async upsertEmailTemplate(payload: dtos.EmailTemplate) {
    const data = {
      action: "add_emailTemplate",
      data: payload,
    };
    const response = await this._client.post(data);
    return response.data;
  }
  async getTextTemplates(containerId: number) {
    const data = {
      action: "get_textTemplates",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }
  async upsertTextTemplate(payload: dtos.TextTemplate) {
    const data = {
      action: "add_textTemplate",
      data: payload,
    };
    const response = await this._client.post(data);
    return response.data;
  }
  async loadContainerTheme(containerId: number) {
    const data = {
      action: "get_containerTheme",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }
  async updateContainerTheme(payload: {
    containerId: number;
    theme: dtos.CustomTheme;
  }) {
    const data = {
      action: "update_containerTheme",
      data: payload,
    };
    const response = await this._client.post(data);
    return response.data;
  }
  async resetContainerTheme(containerId: number) {
    const data = {
      action: "reset_containerTheme",
      data: {
        containerId: containerId,
      },
    };
    const response = await this._client.post(data);
    return response.data;
  }
}
